import React, { useEffect, useState } from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import WarningIcon from "@mui/icons-material/Warning";
import CloseIcon from "@mui/icons-material/Close";
import "./Toast.css";

export type ToastType = "success" | "error" | "warning";

export interface ToastMessage {
  id: string;
  type: ToastType;
  message: string;
  duration?: number;
}

interface ToastProps {
  toast: ToastMessage;
  onRemove: (id: string) => void;
}

const Toast: React.FC<ToastProps> = ({ toast, onRemove }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  useEffect(() => {
    // Show toast with animation
    const showTimer = setTimeout(() => setIsVisible(true), 10);

    // Auto-hide toast after duration
    const hideTimer = setTimeout(() => {
      handleRemove();
    }, toast.duration || 5000);

    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
    };
  }, [toast.duration]);

  const handleRemove = () => {
    setIsRemoving(true);
    setIsVisible(false);
    // Wait for animation to complete before removing
    setTimeout(() => {
      onRemove(toast.id);
    }, 300);
  };

  const getToastIcon = () => {
    switch (toast.type) {
      case "success":
        return <CheckCircleIcon style={{ fontSize: 20 }} />;
      case "error":
        return <CancelIcon style={{ fontSize: 20 }} />;
      case "warning":
        return <WarningIcon style={{ fontSize: 20 }} />;
      default:
        return null;
    }
  };

  return (
    <div
      className={`toast toast-${toast.type} ${
        isVisible ? "toast-visible" : ""
      } ${isRemoving ? "toast-removing" : ""}`}
    >
      <div className="toast-content">
        <div className="toast-icon">{getToastIcon()}</div>
        <div className="toast-message">{toast.message}</div>
        <button
          className="toast-close"
          onClick={handleRemove}
          aria-label="Close toast"
        >
          <CloseIcon style={{ fontSize: 16 }} />
        </button>
      </div>
    </div>
  );
};

export default Toast;
